import { type MouseEventHandler, memo, type ReactNode, useId } from 'react';

import { BoardIcon } from '@air/design-icons';
import { tailwindVariants } from '@air/utils-tailwind';

import { WorkspaceAgentPanelImage } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelImage';
import { WORKSPACE_AGENT_PANEL_PREVIEW_CARD } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';
import { WorkspaceAgentPlayOverlay } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentPlayOverlay';
import { WorkspaceAgentPreviewCardMetaRow } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentPreviewCardMetaRow';

const previewCardFrameTitle = tailwindVariants({
  base: 'min-w-0 break-all font-medium text-12 leading-4',
  variants: {
    tone: {
      pending: 'ads:text-metadata text-gray-10',
      subject: 'ads:text-primary text-gray-12',
      unavailable: 'ads:text-error text-red-11',
    },
  },
});

export type WorkspaceAgentPreviewCardFrameProps = {
  /** Detail rows under the location line. */
  children?: ReactNode;
  /** Passed in so the card stays free of popover context. */
  closeButton?: ReactNode;
  /** Actions under the body, outside the open button because buttons cannot nest. */
  footer?: ReactNode;
  /** A still of timed media reads as playable only if the card says so. */
  hasPlayOverlay?: boolean;
  locationLabel: string | null;
  /** The full trail behind a middle-collapsed `locationLabel`; falls back to the label itself. */
  locationTitle: string | null;
  mediaFallback: ReactNode;
  mediaImageUrl: string | null;
  /** A composer host guards the press so the editor keeps focus and selection. */
  onBodyMouseDown?: MouseEventHandler<HTMLButtonElement>;
  /** Omitted while the subject is unresolved, which is what leaves the body inert. */
  onOpen?: () => void;
  openTestId?: string;
  /** Hover-only, for reporting a bad reference. */
  referenceId?: string;
  testId: string;
  title: string;
  /** Sits on the title line. Omitted by every card except the share-link version chip. */
  titleAdornment?: ReactNode;
  titleTone?: 'pending' | 'subject' | 'unavailable';
};

/**
 * One box for every mention card, so a resolving, resolved and missing subject are the same size. The
 * box is the live region: it outlives the placeholder → subject swap, so the resolved title is
 * announced. `aria-atomic="false"` keeps the board card's second step — its count — from re-reading
 * the title. The body itself is the open target once the subject resolves, rather than a footer button
 * putting a second name on the one thing the card is for.
 */
export const WorkspaceAgentPreviewCardFrame = memo(
  ({
    children,
    closeButton,
    footer,
    hasPlayOverlay = false,
    locationLabel,
    locationTitle,
    mediaFallback,
    mediaImageUrl,
    onBodyMouseDown,
    onOpen,
    openTestId,
    referenceId,
    testId,
    title,
    titleAdornment,
    titleTone = 'subject',
  }: WorkspaceAgentPreviewCardFrameProps) => {
    const detailsId = useId();
    const hasDetails = !!locationLabel || !!children;
    const titleHeading = (
      <span
        className={previewCardFrameTitle({
          className: titleAdornment ? 'min-w-0' : undefined,
          tone: titleTone,
        })}
        title={titleTone === 'subject' ? undefined : referenceId}
      >
        {title}
      </span>
    );
    const body = (
      <>
        {/* The floor is unconditional: a letterboxed still has no height until it loads, so a band sized
            only by its content would collapse and then jump once the image arrives. */}
        <span
          className="relative flex min-h-20 w-full items-center justify-center overflow-hidden ads:bg-subtle bg-gray-3"
          data-testid="WORKSPACE_AGENT_PREVIEW_CARD_MEDIA"
        >
          {mediaImageUrl ? (
            <WorkspaceAgentPanelImage
              {...WORKSPACE_AGENT_PANEL_PREVIEW_CARD}
              alt=""
              // Bracketed: the prototype's letterbox cap, and no spacing token lands on 200px.
              className="block max-h-[200px] max-w-60 object-contain"
              data-testid="WORKSPACE_AGENT_PREVIEW_CARD_MEDIA_IMAGE"
              src={mediaImageUrl}
            />
          ) : (
            <span
              className="relative flex min-h-20 w-full flex-1 items-center justify-center"
              data-testid="WORKSPACE_AGENT_PREVIEW_CARD_MEDIA_FALLBACK"
            >
              {mediaFallback}
            </span>
          )}
          {hasPlayOverlay ? <WorkspaceAgentPlayOverlay /> : null}
        </span>
        <span className="flex flex-col gap-1 px-2.5 py-2">
          {/* A subject's name wraps, so only the fixed placeholder copy carries a tooltip: the reference id. */}
          {titleAdornment ? (
            <span className="flex min-w-0 items-center gap-1.5">
              {titleHeading}
              {titleAdornment}
            </span>
          ) : (
            titleHeading
          )}
          {hasDetails ? (
            <span className="flex flex-col gap-1" id={detailsId}>
              {locationLabel ? (
                <WorkspaceAgentPreviewCardMetaRow
                  icon={<BoardIcon aria-hidden size="xs" />}
                  title={locationTitle ?? locationLabel}
                >
                  {locationLabel}
                </WorkspaceAgentPreviewCardMetaRow>
              ) : null}
              {children}
            </span>
          ) : null}
        </span>
      </>
    );

    return (
      <div
        aria-atomic="false"
        // The prototype's card is elastic between 200px and 240px; no spacing token lands on 200px.
        className="relative w-max min-w-[200px] max-w-60 overflow-hidden rounded-8"
        data-testid={testId}
        role="status"
      >
        {/* Before the body so it stays the card's first tab stop, which is what Radix autofocuses. */}
        {closeButton ? <span className="absolute top-1.5 right-1.5 z-1">{closeButton}</span> : null}
        {onOpen ? (
          <button
            aria-describedby={hasDetails ? detailsId : undefined}
            aria-label={`Open ${title}`}
            className="block w-full cursor-pointer text-left focus-visible:outline-none ads:focus-visible:ring-focus focus-visible:ring-2 focus-visible:ring-altitude-9 focus-visible:ring-inset forced-colors:focus-visible:outline forced-colors:focus-visible:outline-2"
            data-testid={openTestId}
            onClick={onOpen}
            onMouseDown={onBodyMouseDown}
            type="button"
          >
            {body}
          </button>
        ) : (
          body
        )}
        {footer ? <div className="flex flex-col gap-1.5 px-2.5 pb-2.5">{footer}</div> : null}
      </div>
    );
  },
);

WorkspaceAgentPreviewCardFrame.displayName = 'WorkspaceAgentPreviewCardFrame';
